import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Activity, Users, Factory, Search } from "lucide-react";

const fmtTime = (s) => (s ? new Date(s).toLocaleString() : "—");

// Friendly label + tag colour for each logged activity type. Colours mirror the
// warm-ledger palette used elsewhere (Couriers / Follow-ups status tags).
const TYPE_META = {
  customer_created: { label: "Customer created", bg: "#EAF0EA", accent: "#2F4F4F" },
  customer_updated: { label: "Customer updated", bg: "#FFF0E6", accent: "#C85A17" },
  supplier_created: { label: "Supplier created", bg: "#EAF0EA", accent: "#2F4F4F" },
  supplier_updated: { label: "Supplier updated", bg: "#FFF0E6", accent: "#C85A17" },
  import: { label: "Imported", bg: "#EDF1F6", accent: "#1E5FA8" },
  stage_change: { label: "Stage change", bg: "#F1ECF8", accent: "#6D28D9" },
  deal_value_updated: { label: "Value updated", bg: "#FFF0E6", accent: "#C85A17" },
  quote_created: { label: "Quote created", bg: "#EAF0EA", accent: "#2F4F4F" },
  quote_updated: { label: "Quote updated", bg: "#FFF0E6", accent: "#C85A17" },
  email: { label: "Email", bg: "#EDF1F6", accent: "#1E5FA8" },
  whatsapp: { label: "WhatsApp", bg: "#EAF0EA", accent: "#2F4F4F" },
  followup_scheduled: { label: "Follow-up scheduled", bg: "#F1ECF8", accent: "#6D28D9" },
  followup_status: { label: "Follow-up status", bg: "#F1ECF8", accent: "#6D28D9" },
  note: { label: "Note", bg: "#F9F8F6", accent: "#6B6965" },
};

const typeMeta = (t) => TYPE_META[t] || { label: t || "Activity", bg: "#F9F8F6", accent: "#6B6965" };

const RANGES = [
  { value: "", label: "All time" },
  { value: "1", label: "Last 24 hours" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
];

const PAGE_SIZE = 50;

export default function AuditTrail() {
  const [data, setData] = useState({ events: [], summary: [], types: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  // Filters
  const [userId, setUserId] = useState("");
  const [type, setType] = useState("");
  const [days, setDays] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Debounce the free-text search so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = () => {
    const params = { page, page_size: PAGE_SIZE };
    if (userId) params.user_id = userId;
    if (type) params.type = type;
    if (days) params.days = days;
    if (search.trim()) params.search = search.trim();
    return api.get("/admin/activity-feed", { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, userId, type, days, search]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
  const showingFrom = data.total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(page * PAGE_SIZE, data.total || 0);

  const resetFilters = () => { setUserId(""); setType(""); setDays(""); setSearchInput(""); setSearch(""); setPage(1); };
  const hasFilters = userId || type || days || search;

  const selectStyle = { padding: "7px 10px", fontSize: 13, width: "auto" };

  return (
    <div data-testid="audit-trail-page">
      {/* Per-user summary */}
      <div className="overline" style={{ fontSize: 10, marginBottom: 10 }}>Activity by user</div>
      <div className="card-ledger overflow-hidden mb-8">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "var(--surface-2)" }}>
            {["User", "Actions", "Last active", ""].map((h, i) => (
              <th key={i} className="overline" style={{ fontSize: 10, padding: "12px 16px", textAlign: "left", borderBottom: "1px solid var(--border)" }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {data.summary.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: 24, textAlign: "center", color: "var(--text-2)" }}>{loading ? "Loading…" : "No activity recorded yet."}</td></tr>
            ) : data.summary.map((s) => {
              const active = userId === s.user_id;
              return (
                <tr key={s.user_id} style={{ borderBottom: "1px solid var(--border)", background: active ? "var(--surface-2)" : "transparent" }} data-testid={`audit-summary-${s.user_id}`}>
                  <td style={{ padding: "14px 16px", fontWeight: 600, fontSize: 14 }}>{s.user_name || "—"}</td>
                  <td style={{ padding: "14px 16px", fontSize: 13 }} className="font-mono-data">{s.count}</td>
                  <td style={{ padding: "14px 16px", fontSize: 13, color: "var(--text-2)" }}>{fmtTime(s.last_active)}</td>
                  <td style={{ padding: "14px 16px", textAlign: "right" }}>
                    <button
                      className="btn-ghost"
                      style={{ fontSize: 12, padding: "5px 10px" }}
                      onClick={() => { setUserId(active ? "" : s.user_id); setPage(1); }}
                      data-testid={`audit-filter-user-${s.user_id}`}
                    >
                      {active ? "Clear filter" : "View actions"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div style={{ position: "relative", flex: "1 1 260px", minWidth: 200 }}>
          <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-2)" }} />
          <input
            className="input-base"
            placeholder="Search actions, users, or customer / supplier names…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ paddingLeft: 34, width: "100%", height: 38 }}
            data-testid="audit-search"
          />
        </div>
        <select value={userId} onChange={(e) => { setUserId(e.target.value); setPage(1); }} className="input-base" style={selectStyle} data-testid="audit-filter-user">
          <option value="">All users</option>
          {data.summary.map((s) => <option key={s.user_id} value={s.user_id}>{s.user_name || s.user_id}</option>)}
        </select>
        <select value={type} onChange={(e) => { setType(e.target.value); setPage(1); }} className="input-base" style={selectStyle} data-testid="audit-filter-type">
          <option value="">All actions</option>
          {data.types.map((t) => <option key={t} value={t}>{typeMeta(t).label}</option>)}
        </select>
        <select value={days} onChange={(e) => { setDays(e.target.value); setPage(1); }} className="input-base" style={selectStyle} data-testid="audit-filter-range">
          {RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        {hasFilters && (
          <button className="btn-ghost" style={{ fontSize: 12, padding: "7px 12px" }} onClick={resetFilters} data-testid="audit-clear-filters">Clear</button>
        )}
      </div>

      {/* Event log */}
      <div className="card-ledger overflow-hidden">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "var(--surface-2)" }}>
            {["When", "User", "Action", "Account", "Details"].map((h, i) => (
              <th key={i} className="overline" style={{ fontSize: 10, padding: "12px 16px", textAlign: "left", borderBottom: "1px solid var(--border)" }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {data.events.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 32, textAlign: "center", color: "var(--text-2)" }}>
                <Activity size={24} style={{ margin: "0 auto 8px" }} />
                {loading ? "Loading…" : hasFilters ? "No activity matches these filters." : "No activity recorded yet."}
              </td></tr>
            ) : data.events.map((e) => {
              const meta = typeMeta(e.type);
              return (
                <tr key={e.id} style={{ borderBottom: "1px solid var(--border)" }} data-testid={`audit-event-${e.id}`}>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "var(--text-2)", whiteSpace: "nowrap" }}>{fmtTime(e.created_at)}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>{e.by_name || "—"}</td>
                  <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                    <span className="tag" style={{ background: meta.bg, borderColor: "transparent", color: meta.accent, fontWeight: 600 }}>{meta.label}</span>
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 13 }}>
                    {e.entity_name ? (
                      <span className="flex items-center gap-2">
                        {e.entity_type === "supplier"
                          ? <Factory size={13} style={{ color: "var(--text-2)", flexShrink: 0 }} />
                          : <Users size={13} style={{ color: "var(--text-2)", flexShrink: 0 }} />}
                        <span style={{ fontWeight: 600 }}>{e.entity_name}</span>
                        <span className="overline" style={{ fontSize: 9, color: "var(--text-2)" }}>{e.entity_type === "supplier" ? "Supplier" : "Customer"}</span>
                      </span>
                    ) : (
                      <span style={{ color: "var(--text-2)", fontStyle: "italic" }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "var(--text-2)" }}>{e.message || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4" style={{ fontSize: 13, color: "var(--text-2)" }}>
        <span data-testid="audit-range">{data.total === 0 ? "No entries" : `Showing ${showingFrom}–${showingTo} of ${data.total}`}</span>
        <div className="flex items-center gap-2">
          <button className="btn-ghost" style={{ padding: "6px 12px", fontSize: 13 }} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="audit-prev">Previous</button>
          <span className="font-mono-data" style={{ fontSize: 13 }}>{page} / {totalPages}</span>
          <button className="btn-ghost" style={{ padding: "6px 12px", fontSize: 13 }} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="audit-next">Next</button>
        </div>
      </div>
    </div>
  );
}
